import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Trash2, CheckCircle2, XCircle } from "lucide-react";

const STATUS_COLOR = {
  new: "text-amber-700",
  contacted: "text-blue-700",
  closed: "text-slate-500",
};

const HOOKUP_LABELS = {
  toilet: "Toilet", container: "Container", power: "3-Phase Power", water: "Water", lights: "Lighting",
};

export default function LeadsList() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => api.get("/admin/leads").then((r) => setLeads(r.data));

  useEffect(() => {
    reload().catch(() => toast.error("Could not load leads")).finally(() => setLoading(false));
  }, []);

  const setStatus = async (id, status) => {
    const { data } = await api.patch(`/admin/leads/${id}`, { status });
    setLeads((prev) => prev.map((l) => l.id === id ? data : l));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    setLeads((prev) => prev.filter((l) => l.id !== id));
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;

  const counts = leads.reduce((acc, l) => { acc[l.status] = (acc[l.status] || 0) + 1; return acc; }, {});

  return (
    <div data-testid="leads-list">
      <div className="mb-6 flex flex-wrap gap-6 text-sm">
        <span><strong className="text-[#0B0C0E]">{leads.length}</strong> total</span>
        <span className="text-amber-700"><strong>{counts.new || 0}</strong> new</span>
        <span className="text-blue-700"><strong>{counts.contacted || 0}</strong> contacted</span>
        <span className="text-slate-500"><strong>{counts.closed || 0}</strong> closed</span>
      </div>

      <div className="border-2 border-[#0B0C0E] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-[#56606E] bg-[#FCFBF6] border-b border-[#E5E3DA]">
            <tr>
              <th className="p-3">Received</th><th className="p-3">Name</th><th className="p-3">Contact</th>
              <th className="p-3">Size</th><th className="p-3">Hookups</th><th className="p-3">Notes</th>
              <th className="p-3">Status</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} className="border-t border-[#E5E3DA] align-top" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 text-[#56606E] text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="p-3 text-[#0B0C0E]">
                  <div className="font-bold">{l.name}</div>
                  {l.company && <div className="text-xs text-[#56606E]">{l.company}</div>}
                </td>
                <td className="p-3 text-[#2C2F36] text-xs">
                  <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-[#0B0C0E]"><Mail className="w-3 h-3" /> {l.email}</a>
                  <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-[#0B0C0E] mt-1"><Phone className="w-3 h-3" /> {l.phone}</a>
                </td>
                <td className="p-3 text-[#0B0C0E] font-bold">{l.desired_sq_ft.toLocaleString()} sq ft</td>
                <td className="p-3 text-xs">
                  {(l.hookups || []).map((h) => (
                    <span key={h} className="inline-block bg-[#FACC15] text-[#0B0C0E] px-2 py-0.5 mr-1 mb-1 font-bold uppercase">{HOOKUP_LABELS[h] || h}</span>
                  ))}
                </td>
                <td className="p-3 text-xs text-[#2C2F36] max-w-xs">{l.notes || "—"}</td>
                <td className={`p-3 uppercase font-bold ${STATUS_COLOR[l.status]}`}>
                  <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)}
                    className="bg-white border border-[#C6C3B5] text-xs px-2 py-1 text-[#0B0C0E] uppercase font-bold"
                    data-testid={`lead-status-${l.id}`}>
                    <option value="new">new</option>
                    <option value="contacted">contacted</option>
                    <option value="closed">closed</option>
                  </select>
                </td>
                <td className="p-3">
                  <button onClick={() => remove(l.id)} className="text-red-700 hover:bg-red-50 p-1.5" data-testid={`lead-delete-${l.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {!leads.length && (
              <tr><td colSpan="8" className="p-8 text-center text-[#97A1AE]">No leads yet. New custom-size requests will appear here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
